import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { environment } from '../../environments/environment';
import { EditLockEntity, EditLockStatus } from '../models/edit-lock.model';

/**
 * Edit mode for user and business pages. Pages open view-only; acquire() takes the entity's
 * lease (and renews it when called again), release() gives it back. The API refuses writes to a
 * user or business from anyone not holding its lease, so only one admin edits it at a time.
 */
@Injectable({ providedIn: 'root' })
export class EditLockService {
  private readonly baseUrl = environment.subscriptionApiUrl + '/api/admin/edit-locks';

  constructor(private http: HttpClient, private oauthService: OAuthService) { }

  private url(entityType: EditLockEntity, entityId: string): string {
    return `${this.baseUrl}/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`;
  }

  /** Who, if anyone, is editing it. Takes nothing. */
  status(entityType: EditLockEntity, entityId: string): Observable<EditLockStatus> {
    return this.http.get<EditLockStatus>(this.url(entityType, entityId));
  }

  /** Take or renew the lease; when someone else holds it the result says who. */
  acquire(entityType: EditLockEntity, entityId: string): Observable<EditLockStatus> {
    return this.http.post<EditLockStatus>(this.url(entityType, entityId), {});
  }

  release(entityType: EditLockEntity, entityId: string): Observable<void> {
    return this.http.delete<void>(this.url(entityType, entityId));
  }

  /**
   * Release while the page is being torn down (tab closed, reload). An HttpClient request would
   * be cancelled with the page; a keepalive fetch is allowed to finish. Best-effort: if it never
   * arrives, the lease lapses on its own within a couple of minutes.
   */
  releaseOnUnload(entityType: EditLockEntity, entityId: string): void {
    try {
      const token = this.oauthService.getAccessToken();
      fetch(this.url(entityType, entityId), {
        method: 'DELETE',
        keepalive: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      }).catch(() => { /* the lease lapses anyway */ });
    } catch {
      // fetch unavailable or the token store is gone: the lease lapses anyway.
    }
  }
}
